import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import toast from "react-hot-toast";
import { privateAxios } from "../utils/axios";

const emptyRow = () => ({ category: "", product: "", productName: "", quantity: 1, note: "" });

export default function AddServiceParts({ request, onClose, onSuccess }) {
  const [open] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const cancelButtonRef = useRef(null);

  const [allProducts, setAllProducts] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);

  useEffect(() => {
    privateAxios
      .get("/products/all")
      .then((res) => {
        if (res.data?.success && res.data.data) {
          const prods = res.data.data;
          setAllProducts(prods);
          const cats = Array.from(
            new Set(prods.map((p) => p.category).filter(Boolean))
          ).map((cat) => ({ value: cat, label: cat }));
          setCategoryOptions(cats);
        }
      })
      .catch(() => toast.error("Failed to fetch products"));
  }, []);

  const productOptions = (category) =>
    allProducts
      .filter((p) => !category || p.category === category)
      .map((p) => ({ value: p._id, label: p.name }));

  const setRow = (index, patch) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      return updated;
    });
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (index) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const validate = () => {
    const newErrors = {};
    rows.forEach((r, i) => {
      if (!r.category) newErrors[`category_${i}`] = "Category is required";
      if (!r.product) newErrors[`product_${i}`] = "Part is required";
      if (!r.quantity || r.quantity < 1) newErrors[`quantity_${i}`] = "Quantity must be at least 1";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const parts = rows.map((r) => ({
        product: r.product,
        category: r.category,
        productName: r.productName,
        quantity: r.quantity,
        note: r.note,
      }));
      const res = await privateAxios.patch(`/service-requests/${request._id}/parts`, { parts });
      if (res.data.success) {
        toast.success("Parts added successfully");
        onSuccess();
        onClose();
      } else {
        toast.error(res.data.message || "Failed to add parts");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add parts");
    } finally {
      setLoading(false);
    }
  };

  const err = (field) =>
    errors[field] ? (
      <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
    ) : null;

  const selectMenuProps = {
    menuPortalTarget: document.body,
    menuPosition: "fixed",
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all">
                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                  onClick={onClose}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                <Dialog.Title className="text-xl font-semibold text-gray-900 mb-1 text-left">
                  Add Parts Used
                </Dialog.Title>
                <p className="text-sm text-gray-500 mb-6 text-left">
                  {request.branchName} — {request.serviceType}. Parts are only recorded as used in this service; stock is not deducted.
                </p>

                {request.parts?.length > 0 && (
                  <div className="mb-4 text-left">
                    <p className="text-xs font-semibold text-gray-600 mb-1">Already added:</p>
                    <ul className="list-disc list-inside text-xs text-gray-500 space-y-0.5">
                      {request.parts.map((p, i) => (
                        <li key={i}>
                          {p.productName} <span className="text-gray-400">(x{p.quantity})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 text-left">
                  <div>
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 bg-gray-50 p-2 rounded-lg mb-1">
                      <div className="col-span-3">Category</div>
                      <div className="col-span-4">Part / Product</div>
                      <div className="col-span-1">Qty</div>
                      <div className="col-span-3">Note</div>
                      <div className="col-span-1"></div>
                    </div>
                    {rows.map((r, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-start">
                        <div className="col-span-3">
                          <Select
                            options={categoryOptions}
                            placeholder="Category"
                            value={r.category ? { value: r.category, label: r.category } : null}
                            onChange={(s) => setRow(i, { category: s.value, product: "", productName: "" })}
                            classNamePrefix="react-select"
                            {...selectMenuProps}
                          />
                          {err(`category_${i}`)}
                        </div>
                        <div className="col-span-4">
                          <Select
                            options={productOptions(r.category)}
                            placeholder="Select part"
                            value={r.product ? { value: r.product, label: r.productName } : null}
                            onChange={(s) => setRow(i, { product: s.value, productName: s.label })}
                            classNamePrefix="react-select"
                            {...selectMenuProps}
                          />
                          {err(`product_${i}`)}
                        </div>
                        <div className="col-span-1">
                          <input
                            type="number"
                            min={1}
                            value={r.quantity}
                            onChange={(e) => setRow(i, { quantity: Number(e.target.value) })}
                            className={`w-full rounded-md border ${errors[`quantity_${i}`] ? "border-red-300" : "border-gray-300"} px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                          />
                          {err(`quantity_${i}`)}
                        </div>
                        <div className="col-span-3">
                          <input
                            type="text"
                            value={r.note}
                            onChange={(e) => setRow(i, { note: e.target.value })}
                            placeholder="Optional note"
                            className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div className="col-span-1 flex items-center justify-center pt-2">
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRow(i)}
                              className="text-red-500 hover:text-red-700 text-lg leading-none"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addRow}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Add Part
                    </button>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      ref={cancelButtonRef}
                      onClick={onClose}
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {loading ? "Saving..." : "Save Parts"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
